/**
 * S1 · Cover (#cover) — THE FEED homepage, FINAL-SPEC §4 S1.
 *
 *  - M1: registers the cover as a "cover" scene, so the runtime driver writes
 *    --p (0 → 1 over the cover's height); the CSS turns it into the photo
 *    parallax, the phone rise, the text fade and (≥900) the slab hand-off.
 *  - The hero phone plays the heroReels as a story playlist: each reel runs up
 *    to 6 s (or its own length) while its segment in the in-phone story bar
 *    fills; reels crossfade (300 ms, CSS). The fill's animationend is the one
 *    clock that advances the playlist. Runs only while the cover is ≥20 %
 *    visible and autoplay is allowed (no reduced motion / Save-Data); otherwise
 *    the first poster stays with its ▶ "Watch samples" overlay (static markup).
 *  - WCAG 2.2.2: the playlist is moving content that starts by itself and runs
 *    longer than 5 s, so the phone carries a real pause toggle
 *    (button.phone__pause, aria-pressed="true" = paused). It is revealed only
 *    when the playlist actually runs, and the choice holds for the visit.
 */
import {
  canAutoplay,
  ensureSrc,
  pauseReel,
  playReel,
  registerScene,
} from "./runtime";

const MAX_SECONDS = 6;
/** The phone rises at 900 ms in the load sequence (s01-cover.css). */
const INTRO_DELAY = 900;

export function init(): void {
  const cover = document.getElementById("cover");
  if (!cover) return;
  registerScene(cover, "cover");
  initPlaylist(cover);
}

function initPlaylist(cover: HTMLElement): void {
  const phone = cover.querySelector<HTMLElement>("[data-hero-phone]");
  if (!phone) return;
  const reels = Array.from(
    phone.querySelectorAll<HTMLVideoElement>("video.phone__reel"),
  );
  const segs = Array.from(phone.querySelectorAll<HTMLElement>(".phone__seg"));
  const brands = Array.from(
    phone.querySelectorAll<HTMLElement>(".phone__brand"),
  );
  const toggle = phone.querySelector<HTMLButtonElement>(".phone__pause");
  if (reels.length === 0 || !canAutoplay()) return;
  if (!("IntersectionObserver" in window)) return;

  let index = 0;
  let visible = false;
  let started = false;
  let advancing = false;
  /** The visitor pressed pause: nothing resumes until they press play. */
  let paused = false;

  const current = (): HTMLVideoElement | undefined => reels[index];

  const show = (i: number): void => {
    reels.forEach((v, n) => v.classList.toggle("is-on", n === i));
    brands.forEach((b, n) => b.classList.toggle("is-on", n === i));
    segs.forEach((s, n) => {
      s.classList.toggle("is-done", n < i);
      s.classList.remove("is-run");
    });
  };

  /** Start the segment fill once the reel is actually playing. */
  const runSeg = (v: HTMLVideoElement): void => {
    const seg = segs[index];
    if (!seg || seg.classList.contains("is-run")) return;
    const length =
      Number.isFinite(v.duration) && v.duration > 0.5
        ? v.duration
        : MAX_SECONDS;
    const seconds = Math.min(
      MAX_SECONDS,
      length - Math.min(v.currentTime, length - 0.5),
    );
    seg.style.setProperty("--seg-dur", `${seconds.toFixed(2)}s`);
    // restart the animation cleanly
    void seg.offsetWidth;
    seg.classList.add("is-run");
  };

  const next = (): void => {
    if (advancing) return;
    advancing = true;
    const prev = current();
    index = (index + 1) % reels.length;
    const v = current();
    if (!v) return;
    if (index === 0) segs.forEach((s) => s.classList.remove("is-done"));
    show(index);
    try {
      v.currentTime = 0;
    } catch {
      /* not loaded yet */
    }
    if (visible && !paused) playReel(v);
    if (prev && prev !== v) {
      // let the 300ms crossfade finish before the old one stops decoding
      window.setTimeout(() => {
        if (prev !== current()) pauseReel(prev);
      }, 320);
    }
    // warm up the reel after this one
    const after = reels[(index + 1) % reels.length];
    if (after) ensureSrc(after);
    advancing = false;
  };

  for (const v of reels) {
    v.loop = false;
    v.addEventListener("playing", () => {
      if (v !== current()) return;
      if (paused) {
        pauseReel(v);
        return;
      }
      phone.classList.add("is-playing");
      phone.classList.remove("is-held");
      runSeg(v);
    });
    v.addEventListener("waiting", () => {
      if (v === current()) phone.classList.add("is-held");
    });
    v.addEventListener("ended", () => {
      if (v === current()) next();
    });
    v.addEventListener("error", () => {
      if (v === current() && reels.length > 1) next();
    });
  }
  segs.forEach((seg, n) => {
    seg.addEventListener("animationend", () => {
      if (n === index) next();
    });
  });

  const resume = (): void => {
    const v = current();
    if (!v || paused) return;
    phone.classList.remove("is-held");
    if (!playReel(v)) return;
    const after = reels[(index + 1) % reels.length];
    if (after) ensureSrc(after);
  };
  const hold = (): void => {
    const v = current();
    phone.classList.add("is-held");
    if (v) pauseReel(v);
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        if (!started) continue;
        if (visible) resume();
        else hold();
      }
    },
    { threshold: [0, 0.2, 0.4] },
  );
  io.observe(cover);

  if (toggle) {
    toggle.hidden = false;
    toggle.addEventListener("click", () => {
      paused = toggle.getAttribute("aria-pressed") !== "true";
      toggle.setAttribute("aria-pressed", String(paused));
      phone.classList.toggle("is-paused", paused);
      if (paused) hold();
      else if (started && visible && !document.hidden) resume();
    });
  }

  document.addEventListener("visibilitychange", () => {
    if (!started) return;
    if (document.hidden) hold();
    else if (visible) resume();
  });

  const intro =
    document.documentElement.classList.contains("js-motion") &&
    !document.documentElement.classList.contains("no-cover-intro");
  const first = current();
  if (first) ensureSrc(first);
  window.setTimeout(
    () => {
      started = true;
      if (visible && !document.hidden) resume();
    },
    intro ? INTRO_DELAY : 0,
  );
}
